import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type PartnerPermission = 'view_earnings' | 'view_team' | 'manage_settings';

interface FulfillmentPartner {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  website: string | null;
  is_active: boolean;
  delivery_radius_miles: number | null;
  status: 'invited' | 'active' | 'inactive' | 'suspended';
  offers_flowers: boolean;
  offers_cleaning: boolean;
  partner_type: 'independent' | 'monument_company';
  pontis_order_rate: number | null;
  linked_monument_company_id: string | null;
}

interface PartnerAuthState {
  isPartnerUser: boolean;
  isLoading: boolean;
  partner: FulfillmentPartner | null;
  role: 'owner' | 'staff' | null;
  isOwner: boolean;
  permissions: PartnerPermission[];
  hasPermission: (permission: PartnerPermission) => boolean;
  partnerUserId: string | null;
}

const ALL_PERMISSIONS: PartnerPermission[] = ['view_earnings', 'view_team', 'manage_settings'];

export const usePartnerAuth = (): PartnerAuthState => {
  const { user, isLoading: authLoading } = useAuth();
  const [isPartnerUser, setIsPartnerUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [partner, setPartner] = useState<FulfillmentPartner | null>(null);
  const [role, setRole] = useState<'owner' | 'staff' | null>(null);
  const [permissions, setPermissions] = useState<PartnerPermission[]>([]);
  const [partnerUserId, setPartnerUserId] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    const checkPartnerAccess = async () => {
      if (authLoading) return;

      if (!user) {
        setIsPartnerUser(false);
        setPartner(null);
        setRole(null);
        setPermissions([]);
        setPartnerUserId(null);
        setIsLoading(false);
        initialLoadDone.current = false;
        return;
      }

      // Only show loading spinner on initial load, not on re-validations
      if (!initialLoadDone.current) {
        setIsLoading(true);
      }

      try {
        // Check if user is a fulfillment partner user
        const { data: isFulfillmentUser, error: checkError } = await supabase
          .rpc('is_fulfillment_partner_user', { _user_id: user.id });

        if (checkError) {
          console.error('Error checking partner access:', checkError);
          setIsPartnerUser(false);
          setIsLoading(false);
          return;
        }

        setIsPartnerUser(!!isFulfillmentUser);

        if (isFulfillmentUser) {
          // Get the user's partner ID
          const { data: partnerId, error: partnerIdError } = await supabase
            .rpc('get_user_fulfillment_partner', { _user_id: user.id });

          if (partnerIdError || !partnerId) {
            console.error('Error getting partner ID:', partnerIdError);
            setIsLoading(false);
            return;
          }

          // Fetch partner details
          const { data: partnerData, error: partnerError } = await supabase
            .from('fulfillment_partners')
            .select('*')
            .eq('id', partnerId)
            .single();

          if (partnerError) {
            console.error('Error fetching partner:', partnerError);
          } else {
            setPartner(partnerData);
          }

          // Check if user is owner
          const { data: isOwnerResult, error: ownerError } = await supabase
            .rpc('is_fulfillment_partner_owner', { 
              _user_id: user.id, 
              _partner_id: partnerId 
            });

          if (ownerError) {
            console.error('Error checking owner status:', ownerError);
          } else {
            const userIsOwner = !!isOwnerResult;
            setRole(userIsOwner ? 'owner' : 'staff');

            // If owner, grant all permissions
            if (userIsOwner) {
              setPermissions(ALL_PERMISSIONS);
            } else {
              // Fetch staff permissions
              const { data: partnerUserData } = await supabase
                .from('fulfillment_partner_users')
                .select('id')
                .eq('partner_id', partnerId)
                .eq('user_id', user.id)
                .single();

              if (partnerUserData) {
                setPartnerUserId(partnerUserData.id);
                
                const { data: permissionsData, error: permError } = await supabase
                  .from('fulfillment_partner_user_permissions')
                  .select('permission')
                  .eq('partner_user_id', partnerUserData.id);

                if (permError) {
                  console.error('Error fetching permissions:', permError);
                } else {
                  setPermissions((permissionsData || []).map(p => p.permission as PartnerPermission));
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Partner auth error:', error);
        setIsPartnerUser(false);
      } finally {
        setIsLoading(false);
        initialLoadDone.current = true;
      }
    };

    checkPartnerAccess();
  }, [user, authLoading]);

  const hasPermission = (permission: PartnerPermission): boolean => {
    return role === 'owner' || permissions.includes(permission);
  };

  return {
    isPartnerUser,
    isLoading: isLoading || authLoading,
    partner,
    role,
    isOwner: role === 'owner',
    permissions,
    hasPermission,
    partnerUserId,
    isDeactivated: partner ? (!partner.is_active || partner.status !== 'active') : false,
  };
};
